import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { sendToAiSupportSession, getDetailSession } from 'redux/actions/mvp/session.action';
import './create-session.scss';
import '@zoom/videosdk-ui-toolkit/dist/videosdk-ui-toolkit.css';
import { I18n } from '../../../../helpers/i18n';
import micphone from '../../../../static/svg/micphone.svg';
import arrowBackIcon from '../../../../static/svg/back.svg';
import ReactMarkdown from 'react-markdown';
import Webcam from 'react-webcam';

function AiSupportSessionVideo({ user, sessionInfo, joinSession, goBack, getDetailSession }) {
  const [content, setContent] = useState(false);
  const [paragraphs, setParagraphs] = useState([]);
  const [disable, setDisable] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [zoomImage, setZoomImage] = useState(null);
  const [devices, setDevices] = useState([]);
  const [activeDeviceId, setActiveDeviceId] = useState('');
  const [hasCamera, setHasCamera] = useState(false);
  const [error, setError] = useState(null);

  const recognitionRef = useRef(null);
  const isRecognitionRunningRef = useRef(false);
  const silenceTimeoutRef = useRef(null);
  const speechToTextRef = useRef('');
  const conversationIdRef = useRef('');
  const parentMessageIdRef = useRef('');
  const paragraphsRef = useRef([]);
  const webcamRef = useRef(null);

  const SILENCE_TIMEOUT = 3000;


  const renderers = {
    image: ({ src, alt, title }) => (
      <img
      src={src}
      alt={alt}
      title={title}
      style={{ maxWidth: '100%', cursor: 'zoom-in', margin: '10px 0' }}
      onClick={() => setZoomImage(src)}
    />)
  };

  // Lấy danh sách các thiết bị camera
  const handleDevices = useCallback((mediaDevices) => {
    const videoDevices = mediaDevices.filter(({ kind }) => kind === 'videoinput');
    setDevices(videoDevices);
    setHasCamera(videoDevices.length > 0);

    if (videoDevices.length > 0) {
      setActiveDeviceId(videoDevices[0].deviceId);
    } else {
      setError(I18n.t('no_cam'));
    }
  }, []);

  useEffect(() => {
    navigator.mediaDevices.enumerateDevices()
      .then(handleDevices)
      .catch((err) => {
        console.log(err);
        setError(I18n.t('no_cam'));
        setHasCamera(false);
      });
  }, [handleDevices]);

  // Thay đổi camera
  const switchCamera = (deviceId) => {
    setActiveDeviceId(deviceId);
  };

  const videoConstraints = {
    deviceId: activeDeviceId ? { exact: activeDeviceId } : undefined,
    facingMode: activeDeviceId ? undefined : 'user' // Fallback nếu không có deviceId
  };

  useEffect(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      setContent(I18n.t('support_speech'));
      setDisable(true);
    }
  }, []);

  const resetSilenceTimeout = () => {
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
    }

    silenceTimeoutRef.current = setTimeout(async () => {
      console.log('⏱ Tự động dừng sau 3s im lặng');
      await stopListening();
    }, SILENCE_TIMEOUT);
  };

  const clearSilenceTracking = () => {
    if (silenceTimeoutRef.current) clearTimeout(silenceTimeoutRef.current);
  };

  const startListening = () => {
    if (!recognitionRef.current || isRecognitionRunningRef.current) return;

    recognitionRef.current.start();
    isRecognitionRunningRef.current = true;
    setIsListening(true);
    resetSilenceTimeout();
  };

  const stopListening = async () => {
    if (recognitionRef.current && isRecognitionRunningRef.current) {
      recognitionRef.current.stop();
      isRecognitionRunningRef.current = false;
      setIsListening(false);
      clearSilenceTracking();
    }
    if (speechToTextRef.current) {
      console.log('speechToTextRef', speechToTextRef.current);
      const userName = user.name;
      const category = sessionInfo.categoryName;
      const dataAi = await sendToAiSupportSession(speechToTextRef.current, userName, category, conversationIdRef.current, parentMessageIdRef.current);
      console.log('dataAi', dataAi);
      if (dataAi) {
        conversationIdRef.current = dataAi.conversation_id;
        parentMessageIdRef.current = dataAi.message_id;
        setParagraphs([...paragraphsRef.current, dataAi.answer]);
        paragraphsRef.current = [...paragraphsRef.current, dataAi.answer];
      }
    }
  };

  const toggleListening = async () => {
    if (isRecognitionRunningRef.current) {
      await stopListening();
      return;
    }

    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });

      if (!recognitionRef.current) {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        const recognition = new SpeechRecognition();

        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = I18n.language === 'vi' ? 'vi-VN' : 'en-US';

        recognition.onresult = (event) => {
          let interimTranscript = '';
          let finalTranscript = '';
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript + ' ';
            } else {
              interimTranscript += transcript;
            }
          }
          if (finalTranscript || interimTranscript) {
            speechToTextRef.current = finalTranscript || interimTranscript;
            resetSilenceTimeout();
          }
        };

        recognition.onerror = (event) => {
          console.error('Lỗi nhận dạng:', event.error);
          stopListening();
        };

        recognition.onend = () => {
          if (isRecognitionRunningRef.current) {
            recognition.start();
            resetSilenceTimeout();
          } else {
            clearSilenceTracking();
          }
        };

        recognitionRef.current = recognition;
      }

      startListening();
      setDisable(false);
    } catch (error) {
      console.error('Không có quyền micro:', error);
      setContent(I18n.t('microphone_permission_denied'));
      setDisable(true);
    }
  };

    // Chỉ cho phép join khi đến giờ (phiên đang diễn ra). Tắt khi phiên đã kết thúc.
  // sessionInfo được parent cập nhật qua timer (startTime, endTime) nên nút tự bật/tắt đúng lúc.
  const isEnded = sessionInfo?.state === 'is_ended' || sessionInfo?.infoExtra?.state === 'is_ended';
  const canJoin =
    !isEnded &&
    (sessionInfo?.state === 'is_processing' || sessionInfo?.infoExtra?.state === 'is_processing');

  return (
    <div className="container ai-support-session">
      <img
        className="back"
        src={arrowBackIcon}
        alt="Arrow Back"
        onClick={() => goBack()}
      />
      <div className="body">
        <div className="d-flex w-100">
          <div className="left">
            <div className="webcam-container">
              {error && (
                <div className="ml-3 error-message">
                  <p>{error}</p>
                </div>
              )}

              {!hasCamera && !error && (
                <div className="ml-3 no-camera-message">
                  <p>Đang kiểm tra thiết bị camera...</p>
                </div>
              )}

              {hasCamera && (
                <>
                  {devices.length > 1 && (
                    <div className="camera-selector">
                      <select
                        onChange={(e) => switchCamera(e.target.value)}
                        value={activeDeviceId}
                      >
                        {devices.map((device, index) => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {device.label || `Camera ${index + 1}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <Webcam
                    style={{
                      height: "100vh",
                      objectFit: "fill",
                    }}
                    audio={false}
                    ref={webcamRef}
                    videoConstraints={videoConstraints}
                    onUserMediaError={(err) => {
                      console.log(err)
                      setError(I18n.t('no_cam'));
                      setHasCamera(false);
                    }}
                    mirrored={true}
                  />
                </>
              )}
            </div>
          </div>
          <div className="right">
            <span className="title">{I18n.t('help_setting_device')}</span>
            {!paragraphs.length ? (!content ?
                <span>{I18n.t('difficulty_facing', {name: user?.type === 'senior' ? 'Bác' : 'Bạn'})}</span>
                :
                <span>{content}</span>)
              : null
            }
            <div className="content">
              {paragraphs.map((text, index) => (
                <ReactMarkdown key={index} renderers={renderers} linkTarget="_blank">
                  {text}
                </ReactMarkdown>
              ))}
            </div>
            {
              !isListening ?
                <div className="mic" onClick={toggleListening}>
                  <div className={disable ? 'in-active' : ''}>
                    <img src={micphone} alt="micphone" />
                  </div>
                  <span>{I18n.t('press_to_talk')}</span>
                </div>
                :
                <div className="mic" onClick={toggleListening}>
                  <div className="position-relative">
                    <div className="pulse-ring"></div>
                    <div className="pulse-ring"></div>
                    <div className="pulse-ring"></div>
                    <div className="mic-icon"><img src={micphone} alt="micphone" /></div>
                  </div>
                  <span>{I18n.t('press_to_talk')}</span>
                </div>
            }
          </div>
        </div>
        <button
          onClick={() => canJoin && joinSession()}
          className="start"
          disabled={!canJoin}
        >
          {I18n.t('join_session_sort_title')}
        </button>
      </div>
      {zoomImage && (
        <div className="image-popup" onClick={() => setZoomImage(null)}>
          <div className="image-popup-content" onClick={(e) => e.stopPropagation()}>
            <img src={zoomImage} alt="Zoom" />
            <button className="close-btn" onClick={() => setZoomImage(null)}>✕</button>
          </div>
        </div>
      )}
    </div>
  );
}

const mapStateToProps = ({ user }) => ({ user });
const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      sendToAiSupportSession,
      getDetailSession,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(AiSupportSessionVideo);
